/**
 * Appearance prefs — SPEC §5.5 accessibility (text size, reduced motion).
 * Theme (dark/light) lives in theme.ts. Everything here stays local-only.
 */

export type TextSize = "sm" | "md" | "lg";

export interface AppearancePrefs {
  textSize: TextSize;
  /** Minimize transitions/animations (motion-sensitive users). */
  reducedMotion: boolean;
}

const KEY = "veto.appearance.prefs";

export const DEFAULT_APPEARANCE_PREFS: AppearancePrefs = {
  textSize: "md",
  reducedMotion: false,
};

const TEXT_SIZES: readonly TextSize[] = ["sm", "md", "lg"];

export function loadAppearancePrefs(): AppearancePrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_APPEARANCE_PREFS };
    const parsed = JSON.parse(raw) as Partial<AppearancePrefs>;
    return {
      textSize: TEXT_SIZES.includes(parsed.textSize as TextSize)
        ? (parsed.textSize as TextSize)
        : DEFAULT_APPEARANCE_PREFS.textSize,
      reducedMotion: parsed.reducedMotion === true,
    };
  } catch {
    return { ...DEFAULT_APPEARANCE_PREFS };
  }
}

export function saveAppearancePrefs(prefs: AppearancePrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable — session-only */
  }
}

/** Reflect prefs on <html> so CSS can scale text / gate motion app-wide. */
export function applyAppearancePrefs(prefs: AppearancePrefs = loadAppearancePrefs()): void {
  const root = document.documentElement;
  root.dataset.textSize = prefs.textSize;
  root.dataset.motion = prefs.reducedMotion ? "reduced" : "full";
}

/** Enter sends (Shift+Enter: new line). On by default on the web; the phone app's default is off
 *  because phone keyboards have no Shift. Stored locally only. */
const ENTER_KEY = "veto.enterSends";

export function loadEnterSends(): boolean {
  try {
    return localStorage.getItem(ENTER_KEY) !== "0";
  } catch {
    return true;
  }
}

export function saveEnterSends(on: boolean): void {
  try {
    localStorage.setItem(ENTER_KEY, on ? "1" : "0");
  } catch {
    /* private mode */
  }
}
